import { Button, cn, Tab, Tabs } from '@heroui/react'
import { Dropdown } from '@qse/ui'
import { useIsMobile } from '@qse/utils'
import { ArrowDownIcon, GridIcon, ListIcon } from 'lucide-react'
import React, { useEffect } from 'react'

type SortKey = 'name' | 'size' | 'date' | 'trashedDate'
type SortOrder = 'ASC' | 'DESC'
type ViewType = 'grid' | 'list'

const SORT_OPTIONS = [
  { key: 'name', label: 'Name', sortBy: 'name' as const },
  { key: 'size', label: 'Size', sortBy: 'sizeBytes' as const },
  { key: 'date', label: 'Date Created', sortBy: 'createdAt' as const },
]

const TRASH_SORT_OPTIONS = [
  { key: 'name', label: 'Name', sortBy: 'name' as const },
  { key: 'size', label: 'Size', sortBy: 'sizeBytes' as const },
  { key: 'trashedDate', label: 'Trashed Date', sortBy: 'trashedAt' as const },
]

const VIEW_MODES = [
  {
    id: 'grid',
    label: 'Grid',
    icon: <GridIcon className="w-4 h-4 md:w-5 md:h-5" />,
  },
  {
    id: 'list',
    label: 'List',
    icon: <ListIcon className="w-4 h-4 md:w-5 md:h-5" />,
  },
]

interface FileExplorerHeaderProps {
  title: string
  showSortOptions?: boolean
  showViewToggle?: boolean
  trashMode?: boolean
  sortOption: SortKey
  sortOrder: SortOrder
  viewMode: ViewType
  selectedCount?: number
  maxSelectionLimit?: number
  isLoading?: boolean
  onSortOptionChange: (option: string) => void
  onSortOrderToggle: () => void
  onViewModeChange: (mode: ViewType) => void
  showTitle?: boolean
}

const FileExplorerHeader: React.FC<FileExplorerHeaderProps> = ({
  title,
  showSortOptions = true,
  showViewToggle = true,
  trashMode = false,
  sortOption,
  sortOrder,
  viewMode,
  selectedCount = 0,
  maxSelectionLimit,
  isLoading = false,
  onSortOptionChange,
  onSortOrderToggle,
  onViewModeChange,
  showTitle = true,
}) => {
  const isMobile = useIsMobile()

  const handleSortOptionChange = (option: string) => {
    const options = trashMode ? TRASH_SORT_OPTIONS : SORT_OPTIONS
    const found = options.find(opt => opt.label === option || opt.key === option)

    if (found) {
      onSortOptionChange(found.key)
    }
  }

  const handleViewModeChange = (event: React.Key) => {
    const mode = event as ViewType

    onViewModeChange(mode)
  }

  // Validate and normalize sort option on mount/trashMode change
  useEffect(() => {
    const options = trashMode ? TRASH_SORT_OPTIONS : SORT_OPTIONS
    const found = options.find(opt => opt.label === sortOption || opt.key === sortOption)

    onSortOptionChange(found?.key || options[0].key)
  }, [onSortOptionChange, sortOption, trashMode])

  return (
    <div className={showTitle ? 'flex items-center justify-between h-[40px] w-full' : ''}>
      {showTitle && (
        <div className="flex items-center gap-4">
          <h1 className="text-medium font-medium text-gray-900 dark:text-white">{title}</h1>
          {selectedCount > 0 && maxSelectionLimit && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {selectedCount} of {maxSelectionLimit} selected
              </span>
              {selectedCount >= maxSelectionLimit && (
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  (Selection limit reached)
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {(showSortOptions || showViewToggle) && (
        <div className="flex gap-2 md:gap-4 items-center">
          {isLoading && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
              <span className="text-sm text-gray-600 dark:text-gray-400 hidden md:block">
                Loading...
              </span>
            </div>
          )}
          {showSortOptions && (
            <Dropdown
              className="bg-white dark:bg-[#1E1E1E] text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 py-2 px-4 border border-gray-200 dark:border-gray-600"
              hideStartContentItem={true}
              placement="bottom-end"
              selectedOption={`${(trashMode ? TRASH_SORT_OPTIONS : SORT_OPTIONS).find(opt => opt.key === sortOption)?.label || ''}`}
              size={isMobile ? 'sm' : 'md'}
              title="Sort By"
              endContent={
                <Button
                  isIconOnly
                  className="md:!min-w-7 md:!h-7 md:!w-7 !min-w-6 !h-6 !w-6"
                  color="primary"
                  radius="full"
                  size="sm"
                  onPress={onSortOrderToggle}
                >
                  <ArrowDownIcon
                    className={cn(
                      'transition-transform duration-200 w-4 h-4 md:w-5 md:h-5',
                      sortOrder === 'ASC' ? 'rotate-180' : '',
                    )}
                  />
                </Button>
              }
              options={(trashMode ? TRASH_SORT_OPTIONS : SORT_OPTIONS).map(opt => ({
                key: opt.key,
                label: opt.label,
                onSelect: () => {
                  onSortOptionChange(opt.key)
                },
              }))}
              onOptionChange={handleSortOptionChange}
            />
          )}

          {showViewToggle && isMobile ? (
            <Button
              isIconOnly
              className="bg-gray-100 dark:bg-[#343537]"
              size="sm"
              onPress={() => handleViewModeChange(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode == 'grid' ? (
                <ListIcon className="w-4 h-4 md:w-5 md:h-5" />
              ) : (
                <GridIcon className="w-4 h-4 md:w-5 md:h-5" />
              )}
            </Button>
          ) : (
            <Tabs
              aria-label="Dynamic tabs"
              items={VIEW_MODES}
              radius="sm"
              selectedKey={viewMode}
              size={isMobile ? 'sm' : 'lg'}
              classNames={{
                tabList: 'bg-gray-100 dark:bg-[#1E1E1E] border-gray-200 dark:border-gray-600',
                tab: 'text-gray-700 dark:text-gray-300 data-[hover=true]:text-gray-900 dark:data-[hover=true]:text-white',
                tabContent:
                  'text-gray-700 dark:text-gray-300 group-data-[selected=true]:text-gray-900 dark:group-data-[selected=true]:text-white',
                cursor: 'bg-white dark:bg-[#343537] shadow-sm',
              }}
              onSelectionChange={handleViewModeChange}
            >
              {item => (
                <Tab
                  key={item.id}
                  title={
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className=" md:block hidden">{item.label}</span>
                    </div>
                  }
                />
              )}
            </Tabs>
          )}
        </div>
      )}
    </div>
  )
}

export default FileExplorerHeader
